


import React, { useState, useEffect } from "react";

const Vendordersdetails = () => {
    const [showDetails, setShowDetails] = useState({});
    const [orders, setOrders] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [editableAmounts, setEditableAmounts] = useState({});
    const [editMode, setEditMode] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Success popup state
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    // Fetch orders and vendors on component mount
    useEffect(() => {
        fetchOrders();
        fetchVendors();
        debugPermissions();
    }, []);

    // Add success popup auto-close
    useEffect(() => {
        if (showSuccessPopup) {
            const timer = setTimeout(() => {
                setShowSuccessPopup(false);
                setSuccessMessage("");
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [showSuccessPopup]);

    // Add this useEffect to check token validity
    useEffect(() => {
        const checkAuth = () => {
            const authToken = localStorage.getItem("authToken");
            console.log("Auth token on load:", authToken ? "Present" : "Missing");

            // Optional: Decode token to check expiration (if it's a JWT)
            if (authToken && authToken.split('.').length === 3) {
                try {
                    const payload = JSON.parse(atob(authToken.split('.')[1]));
                    console.log("Token payload:", payload);

                    // Check expiration
                    if (payload.exp) {
                        const expirationDate = new Date(payload.exp * 1000);
                        console.log("Token expires:", expirationDate);

                        if (Date.now() >= payload.exp * 1000) {
                            console.log("⚠️ Token has expired!");
                        }
                    }
                } catch (e) {
                    console.log("Could not decode token (not a JWT)");
                }
            }
        };

        checkAuth();
    }, []);

    // Add debug function
    const debugPermissions = async () => {
        const authToken = localStorage.getItem("authToken");
        console.log("=== DEBUG AUTH ===");
        console.log("Token exists:", !!authToken);

        // Test 1: Check if token works for GET requests
        try {
            const getResponse = await fetch(
                "https://delivery.maamaas.com/catering/catering/api/admin/all",
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        Accept: "*/*",
                    },
                }
            );
            console.log("GET /admin/all - Status:", getResponse.status);
            console.log("GET - Success:", getResponse.ok);
        } catch (e) {
            console.log("GET - Error:", e.message);
        }

        // Test 2: Check a sample order's current state
        if (orders.length > 0) {
            const sampleOrder = orders[0];
            const orderId = sampleOrder.id || sampleOrder.orderId;
            console.log("Sample order ID:", orderId);
            console.log("Sample order adminPermision:", sampleOrder.adminPermision);
            console.log("Sample order current budget:", sampleOrder.budget);
        }
    };

    // Fetch orders from API
    const fetchOrders = async () => {
        setLoading(true);
        setError(null);

        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) throw new Error("No auth token found. Please login.");

            console.log("Fetching orders with token...");

            const response = await fetch(
                "https://delivery.maamaas.com/catering/catering/api/admin/all",
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        Accept: "*/*",
                    },
                }
            );

            console.log("Fetch orders response status:", response.status);

            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

            const data = await response.json();
            console.log("Raw API response:", data);

            // Filter only pending approvals (adminPermision === false)
            const filteredData = Array.isArray(data)
                ? data.filter((item) => item.adminPermision === false)
                : [];

            console.log("Filtered pending orders:", filteredData);
            setOrders(filteredData);

            // Initialize editable amounts for each order
            const initialAmounts = {};
            const initialEditMode = {};

            filteredData.forEach(order => {
                const orderId = order.id || order.orderId;
                initialAmounts[orderId] = {
                    budget: order.budget?.toString() || "",
                    paymentAmount: order.paymentAmount?.toString() || ""
                };
                initialEditMode[orderId] = false;
            });

            setEditableAmounts(initialAmounts);
            setEditMode(initialEditMode);

            localStorage.setItem("vendOrdersData", JSON.stringify(filteredData));
        } catch (err) {
            console.error("Error fetching orders:", err);
            setError("Failed to load orders.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch vendors from new API
    const fetchVendors = async () => {
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) return;

            const response = await fetch(
                "https://delivery.maamaas.com/catering/catering/api/admin/get/all",
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        Accept: "*/*",
                    },
                }
            );

            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

            const data = await response.json();
            console.log("Fetched vendors:", data);

            // Process vendors to get company name
            const processedVendors = Array.isArray(data) ? data.map(vendor => ({
                id: vendor.vendorId || vendor.id,
                companyName: vendor.registeredName || vendor.companyName || vendor.name || 'Company Name Not Available',
                vendorId: vendor.vendorId
            })) : [];

            setVendors(processedVendors);
        } catch (err) {
            console.error("Error fetching vendors:", err);
        }
    };

    // Handle edit button click
    const handleEditClick = (orderId) => {
        const order = orders.find(o => (o.id === orderId || o.orderId === orderId));

        // Check if order is in editable state
        if (order.adminPermision === true) {
            setSuccessMessage("❌ Cannot edit - Order already approved");
            setShowSuccessPopup(true);
            return;
        }

        setEditMode(prev => ({
            ...prev,
            [orderId]: true
        }));
    };

    // Handle cancel edit
    const handleCancelEdit = (orderId) => {
        // Reset to original values
        const order = orders.find(o => (o.id === orderId || o.orderId === orderId));
        setEditableAmounts(prev => ({
            ...prev,
            [orderId]: {
                budget: order.budget?.toString() || "",
                paymentAmount: order.paymentAmount?.toString() || ""
            }
        }));

        setEditMode(prev => ({
            ...prev,
            [orderId]: false
        }));
    };

    // Handle amount changes (only when in edit mode)
    const handleAmountChange = (orderId, field, value) => {
        if (!editMode[orderId]) return; // Only allow changes in edit mode

        // Allow only numbers and decimal point
        const regex = /^\d*\.?\d*$/;
        if (value === "" || regex.test(value)) {
            setEditableAmounts(prev => ({
                ...prev,
                [orderId]: {
                    ...prev[orderId],
                    [field]: value
                }
            }));
        }
    };

    // Handle save amount - FIXED WITH BETTER ERROR HANDLING
    const handleSendApproval = async (order, selectedVendorIds = [], assignToAll = false) => {
        const orderId = order.orderId || order.id;
        const authToken = localStorage.getItem("authToken");

        const updatedPaymentAmount =
            Number(editableAmounts[orderId]?.paymentAmount || order.paymentAmount || 0);

        const bodyData = {
            adminPermision: true,
            paymentAmount: updatedPaymentAmount,
            assignToAll: assignToAll,
            vendorIds: assignToAll ? null : selectedVendorIds
        };

        console.log("FINAL APPROVAL BODY:", bodyData);

        try {
            const response = await fetch(
                `https://delivery.maamaas.com/catering/catering/api/admin/approval/${orderId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${authToken}`,
                    },
                    body: JSON.stringify(bodyData),
                }
            );

            const responseText = await response.text();
            console.log("Approval Response:", response.status, responseText);

            if (!response.ok) {
                throw new Error(responseText || `HTTP ${response.status}`);
            }

            setSuccessMessage(
                assignToAll
                    ? "✅ Order approved and sent to all vendors!"
                    : `✅ Order approved and sent to ${selectedVendorIds.length} vendor(s)!`
            );
            setShowSuccessPopup(true);

            setOrders(prev =>
                prev.map(item =>
                    (item.id === orderId || item.orderId === orderId)
                        ? { ...item, adminPermision: true }
                        : item
                )
            );

        } catch (err) {
            console.error("Approval Error:", err);
            setSuccessMessage("❌ " + err.message);
            setShowSuccessPopup(true);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return dateString;
    };

    // Calculate total plates
    const getTotalPlates = (order) => {
        return (order.vegPlates || 0) + (order.nonVegPlates || 0) + (order.mixedPlates || 0);
    };

    // Format location
    const formatLocation = (order) => {
        const parts = [];
        if (order.fullAddress) parts.push(order.fullAddress);
        if (order.city) parts.push(order.city);
        if (order.state) parts.push(order.state);
        if (order.pincode) parts.push(order.pincode);
        if (order.country) parts.push(order.country);
        return parts.join(', ');
    };

    // Check if item is non-veg
    const isNonVeg = (item) => {
        if (!item || typeof item !== 'string') return false;
        const itemLower = item.toLowerCase();
        const nonVegKeywords = ['chicken', 'chiken', 'mutton', 'fish', 'egg', 'prawn', 'meat', 'beef', 'pork'];
        return nonVegKeywords.some(keyword => itemLower.includes(keyword));
    };

    // Format add-on name for display
    const formatAddOnName = (addOnType) => {
        if (!addOnType) return '';
        return addOnType
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const styles = {
        card: {
            border: '1px solid #ccc',
            padding: '16px',
            margin: '16px',
            marginTop: "25px",
            borderRadius: '10px',
            marginRight: "5px",
            marginLeft: "5px",
            background: "linear-gradient(to right, #f46b45, #eea849)"
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px'
        },
        orderId: { margin: 0, fontWeight: "bold" },
        dropdownBtn: {
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            color: '#333'
        },
        pickupBtn: {
            background: '#28a745',
            color: '#fff',
            border: 'none',
            padding: '8px 20px',
            cursor: 'pointer',
            borderRadius: '5px',
            fontWeight: "bold",
            fontSize: "16px"
        },
        vegBadge: {
            backgroundColor: "#e6f7e6",
            color: "#2e7d32",
            padding: "2px 8px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: "bold",
            marginLeft: "8px",
            display: "inline-block"
        },
        nonVegBadge: {
            backgroundColor: "#ffe6e6",
            color: "#d32f2f",
            padding: "2px 8px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: "bold",
            marginLeft: "8px",
            display: "inline-block"
        },
        amountDisplay: {
            width: "150px",
            padding: "8px 12px",
            border: "2px solid #e0e0e0",
            borderRadius: "6px",
            fontSize: "16px",
            fontWeight: "500",
            backgroundColor: "#f8f9fa",
            color: "#333",
            display: "inline-block"
        },
        amountInput: {
            width: "150px",
            padding: "8px 12px",
            border: "2px solid rgb(230 109 51)",
            borderRadius: "6px",
            fontSize: "16px",
            fontWeight: "500",
            outline: "none",
            transition: "all 0.3s ease",
            backgroundColor: "white"
        },
        editButton: {
            backgroundColor: "#ffc107",
            color: "#333",
            border: "none",
            padding: "8px 20px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "14px",
            marginLeft: "10px",
            transition: "all 0.3s ease"
        },
        saveButton: {
            backgroundColor: "rgb(230 109 51)",
            color: "white",
            border: "none",
            padding: "8px 20px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "14px",
            marginLeft: "10px",
            transition: "all 0.3s ease"
        },
        cancelButton: {
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            padding: "8px 20px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "14px",
            marginLeft: "10px",
            transition: "all 0.3s ease"
        }
    };

    if (loading) return (
        <div style={{
            textAlign: "center",
            padding: "40px",
            fontSize: "18px",
            color: "#666"
        }}>
            Loading orders...
        </div>
    );

    if (error) return (
        <div style={{
            textAlign: "center",
            padding: "40px",
            fontSize: "18px",
            color: "#ff4444"
        }}>
            {error}
        </div>
    );

    return (
        <div style={{ position: 'relative', height: '100%' }}>
            {/* Success Popup */}
            {showSuccessPopup && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    backgroundColor: successMessage.includes('❌') ? '#f44336' : '#4CAF50',
                    color: 'white',
                    padding: '15px 25px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    animation: 'slideInRight 0.3s ease, fadeOut 0.3s ease 1.7s'
                }}>
                    <span style={{ fontSize: '20px' }}>{successMessage.includes('✅') ? '✓' : '✗'}</span>
                    <span style={{ fontWeight: 'bold' }}>{successMessage}</span>
                </div>
            )}

            {/* Main Scrollable Container */}
            <div style={{
                height: 'calc(90vh - 150px)', // Adjust based on your layout
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingRight: '10px',
                scrollbarWidth: 'thin',
                scrollbarColor: '#888 #f1f1f1'
            }}>
                {orders.length === 0 ? (
                    <div style={{
                        textAlign: "center",
                        padding: "40px",
                        fontSize: "18px",
                        color: "#666"
                    }}>
                        No pending orders found
                    </div>
                ) : (
                    orders.map((order) => {
                        const orderId = order.id || order.orderId;
                        const totalPlates = getTotalPlates(order);
                        const location = formatLocation(order);

                        const currentAmounts = editableAmounts[orderId] || {
                            budget: order.budget?.toString() || "",
                            paymentAmount: order.paymentAmount?.toString() || ""
                        };
                        const isEditing = editMode[orderId] || false;

                        return (
                            <div key={orderId} style={styles.card}>
                                {/* ===== Header ===== */}
                                <div style={styles.header}>
                                    <div>
                                        <h3 style={styles.orderId}>Enquiry Id: {orderId}</h3>
                                    </div>

                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <p style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "0rem" }}>
                                            Budget:
                                        </p>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={currentAmounts.budget}
                                                onChange={(e) => handleAmountChange(orderId, 'budget', e.target.value)}
                                                placeholder="Enter budget"
                                                style={styles.amountInput}
                                            />
                                        ) : (
                                            <span style={styles.amountDisplay}>
                                                ₹{currentAmounts.budget || '0'}
                                            </span>
                                        )}

                                        {!isEditing && !order.adminPermision && (
                                            <button
                                                onClick={() => handleEditClick(orderId)}
                                                style={styles.editButton}
                                            >
                                                Edit
                                            </button>
                                        )}
                                    </div>

                                    <button
                                        style={styles.dropdownBtn}
                                        onClick={() =>
                                            setShowDetails((prev) => ({
                                                ...prev,
                                                [orderId]: !prev[orderId],
                                            }))
                                        }
                                    >
                                        {showDetails[orderId] ? "▲" : "▼"}
                                    </button>
                                </div>

                                {/* ===== Order Details ===== */}
                                <div
                                    style={{
                                        marginTop: "0px",
                                        background: "linear-gradient(to right, rgb(244 107 68), rgb(238, 168, 73))",
                                        padding: "0px",
                                        borderRadius: "5px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        flexWrap: "wrap",
                                        color: "#000",
                                        fontSize: "16px",
                                        gap: "40px",
                                        marginLeft: "30px",
                                    }}
                                >
                                    {/* Left Column */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "0px" }}>
                                            <span style={{ minWidth: "120px" }}><strong>Category</strong></span>
                                            <span style={{ minWidth: "20px" }}>:</span>
                                            <span style={{ color: "black" }}>
                                                {order.event || "N/A"}
                                            </span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "0px" }}>
                                            <span style={{ minWidth: "120px" }}><strong>Event Type</strong></span>
                                            <span style={{ minWidth: "20px" }}>:</span>
                                            <span style={{ color: "black" }}>
                                                {order.eventType || "N/A"}
                                            </span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "0px" }}>
                                            <span style={{ minWidth: "120px" }}><strong>Date</strong></span>
                                            <span style={{ minWidth: "20px" }}>:</span>
                                            <span>{formatDate(order.eventDate)}</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "0px" }}>
                                            <span style={{ minWidth: "120px" }}><strong>Time</strong></span>
                                            <span style={{ minWidth: "20px" }}>:</span>
                                            <span>{order.eventTime || "Not specified"}</span>
                                        </div>
                                    </div>

                                    {/* Middle Column */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "0px" }}>
                                            <span style={{ minWidth: "120px" }}><strong>Name</strong></span>
                                            <span style={{ minWidth: "20px" }}>:</span>
                                            <span>{order.fullName || order.name}</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "0px" }}>
                                            <span style={{ minWidth: "120px" }}><strong>Mobile</strong></span>
                                            <span style={{ minWidth: "20px" }}>:</span>
                                            <span>{order.phoneNumber || order.mobile}</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", marginBottom: "0px" }}>
                                            <span style={{ minWidth: "120px" }}><strong>Email</strong></span>
                                            <span style={{ minWidth: "20px" }}>:</span>
                                            <span>{order.email}</span>
                                        </div>
                                        {/* <div style={{ display: "flex", alignItems: "center", marginBottom: "0px" }}>
                                        <span style={{ minWidth: "120px" }}><strong>Plates</strong></span>
                                        <span style={{ minWidth: "20px" }}>:</span>
                                        <span>{totalPlates}</span>
                                    </div> */}
                                    </div>

                                    {/* Right Column */}
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "6px",
                                            marginRight: "40px",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            <span style={{ minWidth: "120px", fontWeight: "bold" }}>Location</span>
                                            <span style={{ minWidth: "20px" }}>:</span>
                                            <span>
                                                {location}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* ===== Order Items Table (expandable) ===== */}
                                {showDetails[orderId] && (
                                    <div>
                                        {/* Plates Summary Table */}
                                        <div style={{ marginTop: "15px" }}>
                                            <table
                                                style={{
                                                    width: "100%",
                                                    borderCollapse: "collapse",
                                                    marginTop: "0.5rem",
                                                }}
                                                className="leads-plates-table"
                                            >
                                                <thead>
                                                    <tr style={{
                                                        backgroundColor: "rgb(230 109 51)",
                                                        textAlign: "center",
                                                        color: "white"
                                                    }}>
                                                        <th style={{ padding: "10px", border: "1px solid #ddd" }}>Veg Plates</th>
                                                        <th style={{ padding: "10px", border: "1px solid #ddd" }}>Non-Veg Plates</th>
                                                        {/* <th style={{ padding: "10px", border: "1px solid #ddd" }}>Mixed Plates</th> */}
                                                        <th style={{
                                                            padding: "10px",
                                                            border: "1px solid #ddd",
                                                        }}>Total Plates</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr style={{
                                                        textAlign: "center",
                                                        fontWeight: "bold",
                                                        fontSize: "16px",
                                                        backgroundColor: "#e9f5e9"
                                                    }}>
                                                        <td style={{
                                                            padding: "12px",
                                                            border: "1px solid #ddd",
                                                            color: "#22863a"
                                                        }}>
                                                            {order.vegPlates || 0}
                                                        </td>
                                                        <td style={{
                                                            padding: "12px",
                                                            border: "1px solid #ddd",
                                                            color: "#d73a49"
                                                        }}>
                                                            {order.nonVegPlates || 0}
                                                        </td>
                                                        {/* <td style={{
                                                        padding: "12px",
                                                        border: "1px solid #ddd",
                                                        color: "#005cc5"
                                                    }}>
                                                        {order.mixedPlates || 0}
                                                    </td> */}
                                                        <td style={{
                                                            padding: "12px",
                                                            border: "1px solid #ddd",
                                                            color: "black",
                                                            fontSize: "18px"
                                                        }}>
                                                            {totalPlates}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* SIMPLIFIED MENU ITEMS SECTION */}
                                        {order.items && typeof order.items === 'object' && (
                                            <div style={{ marginTop: "15px" }}>
                                                <div style={{
                                                    backgroundColor: "#f8f9fa",
                                                    border: "1px solid #dee2e6",
                                                    borderRadius: "6px",
                                                    padding: "0",
                                                    overflow: "hidden"
                                                }}>
                                                    {/* Heading */}
                                                    <div style={{
                                                        backgroundColor: "#e9ecef",
                                                        color: "#495057",
                                                        padding: "10px 15px",
                                                        fontSize: "15px",
                                                        fontWeight: "600",
                                                        borderBottom: "1px solid #dee2e6"
                                                    }}>
                                                        Menu Items
                                                    </div>

                                                    {/* Display items directly from the items object */}
                                                    <div style={{ padding: "15px" }}>
                                                        {Object.entries(order.items).map(([category, items]) => {
                                                            if (!Array.isArray(items)) return null;

                                                            return (
                                                                <div key={category} style={{ marginBottom: "20px" }}>
                                                                    <h5 style={{
                                                                        color: "rgb(230 109 51)",
                                                                        marginBottom: "10px",
                                                                        fontSize: "16px",
                                                                        fontWeight: "600",
                                                                        borderBottom: "2px solid rgb(230 109 51)",
                                                                        paddingBottom: "5px"
                                                                    }}>
                                                                        {category} ({items.length})
                                                                    </h5>

                                                                    <div style={{
                                                                        display: "grid",
                                                                        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                                                                        gap: "10px"
                                                                    }}>
                                                                        {items.map((item, index) => {
                                                                            const nonVeg = isNonVeg(item);
                                                                            return (
                                                                                <div
                                                                                    key={index}
                                                                                    style={{
                                                                                        backgroundColor: "white",
                                                                                        border: nonVeg ? "1px solid #ffcdd2" : "1px solid #c8e6c9",
                                                                                        borderRadius: "5px",
                                                                                        padding: "12px",
                                                                                        fontSize: "14px",
                                                                                        color: "#212529",
                                                                                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "space-between",
                                                                                        borderLeft: nonVeg ? "4px solid #f44336" : "4px solid #4caf50"
                                                                                    }}
                                                                                >
                                                                                    <div style={{ fontWeight: "500" }}>
                                                                                        {item}
                                                                                    </div>
                                                                                    <span style={nonVeg ? styles.nonVegBadge : styles.vegBadge}>
                                                                                        {nonVeg ? 'Non-Veg' : 'Veg'}
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Add-Ons Section */}
                                        {order.addOns && Array.isArray(order.addOns) && order.addOns.length > 0 && (
                                            <div style={{ marginTop: "15px" }}>
                                                <div style={{
                                                    backgroundColor: "#f8f9fa",
                                                    border: "1px solid #dee2e6",
                                                    borderRadius: "6px",
                                                    padding: "0",
                                                    overflow: "hidden"
                                                }}>
                                                    {/* Add-Ons Heading */}
                                                    <div style={{
                                                        backgroundColor: "#e9ecef",
                                                        color: "#495057",
                                                        padding: "10px 15px",
                                                        fontSize: "15px",
                                                        fontWeight: "600",
                                                        borderBottom: "1px solid #dee2e6"
                                                    }}>
                                                        Add-Ons ({order.addOns.filter(addon => addon.selected).length})
                                                    </div>

                                                    {/* Add-Ons Grid */}
                                                    <div style={{
                                                        padding: "15px",
                                                        display: "grid",
                                                        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                                                        gap: "10px"
                                                    }}>
                                                        {order.addOns
                                                            .filter(addon => addon.selected)
                                                            .map((addon, index) => (
                                                                <div
                                                                    key={index}
                                                                    style={{
                                                                        backgroundColor: "white",
                                                                        border: "1px solid #d4edda",
                                                                        borderRadius: "5px",
                                                                        padding: "12px",
                                                                        fontSize: "14px",
                                                                        color: "#155724",
                                                                        textAlign: "center",
                                                                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "space-between",
                                                                        minHeight: "45px",
                                                                        borderLeft: "4px solid #28a745"
                                                                    }}
                                                                >
                                                                    <div style={{ fontWeight: "600" }}>
                                                                        {formatAddOnName(addon.addOnType)}
                                                                    </div>
                                                                    <div style={{
                                                                        backgroundColor: "#28a745",
                                                                        color: "white",
                                                                        borderRadius: "20px",
                                                                        padding: "2px 10px",
                                                                        fontSize: "12px",
                                                                        fontWeight: "bold"
                                                                    }}>
                                                                        Qty: {addon.quantity}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Additional Requests */}
                                        {order.additionalRequests && (
                                            <div style={{ marginTop: "15px" }}>
                                                <div style={{
                                                    backgroundColor: "#fff3cd",
                                                    border: "1px solid #ffeaa7",
                                                    borderRadius: "6px",
                                                    padding: "12px 15px",
                                                    color: "#856404",
                                                    fontSize: "14px"
                                                }}>
                                                    <span style={{ fontWeight: "bold" }}>Additional Requests: </span>
                                                    {order.additionalRequests}
                                                </div>
                                            </div>
                                        )}

                                        {/* Budget and Payment Amount Section */}
                                        <div style={{
                                            marginTop: "15px",
                                            backgroundColor: "#f8f9fa",
                                            border: "1px solid #dee2e6",
                                            borderRadius: "6px",
                                            padding: "15px"
                                        }}>
                                            <div style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                flexWrap: "wrap",
                                                gap: "20px"
                                            }}>
                                                <div style={{ flex: 1, minWidth: "200px" }}>
                                                    <div style={{
                                                        fontSize: "13px",
                                                        color: "#6c757d",
                                                        marginBottom: "4px",
                                                        fontWeight: "500"
                                                    }}>
                                                        Budget
                                                    </div>
                                                    <div style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "10px"
                                                    }}>
                                                        <span style={{ fontSize: "16px", fontWeight: "bold" }}>₹</span>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={currentAmounts.budget}
                                                                onChange={(e) => handleAmountChange(orderId, 'budget', e.target.value)}
                                                                placeholder="Enter amount"
                                                                style={{
                                                                    ...styles.amountInput,
                                                                    width: "150px",
                                                                    fontSize: "18px"
                                                                }}
                                                            />
                                                        ) : (
                                                            <span style={styles.amountDisplay}>
                                                                {currentAmounts.budget || '0'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div style={{ flex: 1, minWidth: "200px" }}>
                                                    <div style={{
                                                        fontSize: "13px",
                                                        color: "#6c757d",
                                                        marginBottom: "4px",
                                                        fontWeight: "500"
                                                    }}>
                                                        Including Lead Price
                                                    </div>
                                                    <div style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "10px"
                                                    }}>
                                                        <span style={{ fontSize: "16px", fontWeight: "bold" }}>₹</span>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={currentAmounts.paymentAmount}
                                                                onChange={(e) => handleAmountChange(orderId, 'paymentAmount', e.target.value)}
                                                                placeholder="Enter amount"
                                                                style={{
                                                                    ...styles.amountInput,
                                                                    width: "150px",
                                                                    fontSize: "18px",
                                                                    borderColor: "rgb(230 109 51)"
                                                                }}
                                                            />
                                                        ) : (
                                                            <span style={styles.amountDisplay}>
                                                                {currentAmounts.paymentAmount || '0'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    {isEditing && (
                                                        <button
                                                            onClick={() => handleCancelEdit(orderId)}
                                                            style={styles.cancelButton}
                                                        >
                                                            Cancel
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Vendor Selection Component with company name only */}
                                        <OrderVendorSelection
                                            vendors={vendors}
                                            order={order}
                                            onSendApproval={handleSendApproval}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Custom Scrollbar Styles */}
            <style>{`
            @keyframes slideInRight {
                from {
                    opacity: 0;
                    transform: translateX(100%);
                }
                to {
                    opacity: 1;
                    transform: translateX(0);
                }
            }

            @keyframes fadeOut {
                from {
                    opacity: 1;
                }
                to {
                    opacity: 0;
                }
            }

            /* Webkit scrollbar styles */
            div::-webkit-scrollbar {
                width: 8px;
                height: 8px;
            }

            div::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 4px;
            }

            div::-webkit-scrollbar-thumb {
                background: #888;
                border-radius: 4px;
            }

            div::-webkit-scrollbar-thumb:hover {
                background: #555;
            }
        `}</style>
        </div>
    );
};

// Simplified Vendor Selection Component - Company Name Only
const OrderVendorSelection = ({ vendors, order, onSendApproval }) => {
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isApproved, setIsApproved] = useState(false); // State to track approval status

    // Auto-close success popup
    useEffect(() => {
        if (showSuccessPopup) {
            const timer = setTimeout(() => {
                setShowSuccessPopup(false);
                setSuccessMessage("");
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [showSuccessPopup]);

    // Handle checkbox selection
    const handleSelectVendor = (vendorId) => {
        if (isApproved) return; // Prevent selection if already approved

        setSelectedVendors(prev => {
            if (prev.includes(vendorId)) {
                return prev.filter(id => id !== vendorId);
            } else {
                return [...prev, vendorId];
            }
        });
    };

    // Handle select all in dropdown
    const handleSelectAll = () => {
        if (isApproved) return; // Prevent select all if already approved

        if (selectedVendors.length === filteredVendors.length && filteredVendors.length > 0) {
            // Deselect all filtered
            const filteredIds = filteredVendors.map(v => v.id);
            setSelectedVendors(prev => prev.filter(id => !filteredIds.includes(id)));
        } else {
            // Select all filtered
            const filteredIds = filteredVendors.map(v => v.id);
            setSelectedVendors(prev => {
                const newSelected = [...new Set([...prev, ...filteredIds])];
                return newSelected;
            });
        }
    };

    // Filter vendors based on search (company name only)
    const filteredVendors = (vendors || []).filter(vendor => {
        const searchLower = searchTerm.toLowerCase();
        return vendor.companyName?.toLowerCase().includes(searchLower);
    });

    // Send to all vendors
    const handleSendToAllVendors = () => {
        if (isApproved) return; // Prevent action if already approved

        onSendApproval(order, [], true);
        setShowDropdown(false);
        setSelectedVendors([]);
        setIsApproved(true); // Set approved state to true
    };

    // Handle approve after selection
    const handleApprove = () => {
        if (isApproved) return; // Prevent action if already approved

        if (selectedVendors.length === 0) {
            setSuccessMessage("❌ Please select at least one vendor to approve");
            setShowSuccessPopup(true);
            return;
        }

        onSendApproval(order, selectedVendors, false);
        setShowDropdown(false);
        setIsApproved(true); // Set approved state to true
    };

    const buttonContainerStyle = {
        display: "flex",
        justifyContent: "center",
        gap: "20px",
        marginBottom: "20px",
        flexWrap: "wrap"
    };

    const primaryButtonStyle = {
        backgroundColor: isApproved ? "#6c757d" : "#28a745", // Gray if approved
        color: "#fff",
        border: "none",
        padding: "12px 30px",
        borderRadius: "6px",
        cursor: isApproved ? "not-allowed" : "pointer",
        fontWeight: "bold",
        fontSize: "16px",
        minWidth: "180px",
        transition: "all 0.3s ease",
        opacity: isApproved ? 0.7 : 1
    };

    const secondaryButtonStyle = {
        backgroundColor: isApproved ? "#6c757d" : "#ffc107", // Gray if approved
        color: isApproved ? "#fff" : "#333",
        border: "none",
        padding: "12px 30px",
        borderRadius: "6px",
        cursor: isApproved ? "not-allowed" : "pointer",
        fontWeight: "bold",
        fontSize: "16px",
        minWidth: "180px",
        transition: "all 0.3s ease",
        opacity: isApproved ? 0.7 : 1
    };

    const approveButtonStyle = {
        backgroundColor: isApproved ? "#6c757d" : "rgb(230 109 51)", // Gray if approved
        color: "#fff",
        border: "none",
        padding: "14px 40px",
        borderRadius: "6px",
        cursor: isApproved ? "not-allowed" : "pointer",
        fontWeight: "bold",
        fontSize: "18px",
        marginTop: "20px",
        width: "100%",
        maxWidth: "400px",
        marginLeft: "auto",
        marginRight: "auto",
        display: "block",
        transition: "all 0.3s ease",
        opacity: isApproved ? 0.7 : 1
    };

    const approvedContainerStyle = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: "20px"
    };

    const approvedBadgeStyle = {
        backgroundColor: "#28a745",
        color: "white",
        padding: "14px 40px",
        borderRadius: "6px",
        fontWeight: "bold",
        fontSize: "18px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        width: "100%",
        maxWidth: "400px"
    };

    const dropdownStyle = {
        marginTop: "15px",
        border: "1px solid #ddd",
        borderRadius: "8px",
        backgroundColor: "white",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        maxHeight: "400px",
        overflow: "hidden",
        display: showDropdown && !isApproved ? "block" : "none" // Hide dropdown if approved
    };

    const dropdownHeaderStyle = {
        padding: "15px",
        borderBottom: "1px solid #eee",
        backgroundColor: "#f8f9fa"
    };

    const searchInputStyle = {
        width: "100%",
        padding: "10px",
        border: "1px solid #ddd",
        borderRadius: "4px",
        fontSize: "14px",
        marginBottom: "10px"
    };

    const selectAllStyle = {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "10px 0",
        borderBottom: "1px solid #eee",
        cursor: "pointer"
    };

    const vendorItemStyle = {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 15px",
        borderBottom: "1px solid #f0f0f0",
        cursor: "pointer",
        transition: "background-color 0.2s"
    };

    const companyNameStyle = {
        fontWeight: "600",
        color: "#333",
        fontSize: "15px"
    };

    return (
        <div style={{ marginTop: "20px", borderTop: "2px solid rgb(230 109 51)", paddingTop: "15px", position: "relative" }}>
            {/* Success Popup for vendor actions */}
            {showSuccessPopup && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    backgroundColor: successMessage.includes('❌') ? '#f44336' : '#4CAF50',
                    color: 'white',
                    padding: '15px 25px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    animation: 'slideInRight 0.3s ease, fadeOut 0.3s ease 1.7s'
                }}>
                    <span style={{ fontSize: '20px' }}>{successMessage.includes('✅') ? '✓' : '✗'}</span>
                    <span style={{ fontWeight: 'bold' }}>{successMessage}</span>
                </div>
            )}

            <h4 style={{ marginBottom: "15px", color: "#333", textAlign: "center" }}>Select Vendors for Order Assignment</h4>

            {/* Centered Action Buttons */}
            {isApproved ? (
                // Show only one approved badge when approved
                <div style={approvedContainerStyle}>
                    <div style={approvedBadgeStyle}>
                        <span>✓</span> Approved
                    </div>
                </div>
            ) : (
                // Show both buttons when not approved
                <div style={buttonContainerStyle}>
                    <button
                        onClick={handleSendToAllVendors}
                        style={primaryButtonStyle}
                    >
                        All Vendors
                    </button>
                    <button
                        onClick={() => setShowDropdown(!showDropdown)}
                        style={secondaryButtonStyle}
                    >
                        Select Vendors {selectedVendors.length > 0 ? `(${selectedVendors.length})` : ''}
                    </button>
                </div>
            )}

            {/* Dropdown with Vendor List */}
            <div style={dropdownStyle}>
                <div style={dropdownHeaderStyle}>
                    {/* Search Bar - Company Name only */}
                    <input
                        type="text"
                        placeholder="Search by company name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={searchInputStyle}
                    />

                    {/* Select All Option */}
                    <div
                        style={selectAllStyle}
                        onClick={handleSelectAll}
                    >
                        <input
                            type="checkbox"
                            checked={filteredVendors.length > 0 && selectedVendors.length === filteredVendors.length}
                            onChange={handleSelectAll}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <span style={{ fontWeight: "500" }}>
                            Select All {filteredVendors.length} {filteredVendors.length === 1 ? 'Vendor' : 'Vendors'}
                        </span>
                        <span style={{ marginLeft: "auto", color: "#666", fontSize: "13px" }}>
                            {selectedVendors.length} selected
                        </span>
                    </div>
                </div>

                {/* Vendor List with Scroll - Company Name Only */}
                <div style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    padding: "5px 0"
                }}>
                    {filteredVendors.length > 0 ? (
                        filteredVendors.map((vendor) => (
                            <div
                                key={vendor.id}
                                style={{
                                    ...vendorItemStyle,
                                    backgroundColor: selectedVendors.includes(vendor.id) ? "#f0e6ff" : "white",
                                    borderLeft: selectedVendors.includes(vendor.id) ? "4px solid rgb(230 109 51)" : "4px solid transparent"
                                }}
                                onClick={() => handleSelectVendor(vendor.id)}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedVendors.includes(vendor.id)}
                                    onChange={() => handleSelectVendor(vendor.id)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div style={companyNameStyle}>
                                    {vendor.companyName}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                            No vendors found matching "{searchTerm}"
                        </div>
                    )}
                </div>

                {/* Selected Count Footer */}
                <div style={{
                    padding: "10px 15px",
                    borderTop: "1px solid #eee",
                    backgroundColor: "#f8f9fa",
                    fontSize: "13px",
                    color: "#666",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <span>{selectedVendors.length} vendor{selectedVendors.length !== 1 ? 's' : ''} selected</span>
                    <span>Total: {vendors.length} vendors</span>
                </div>
            </div>

            {/* Approve Button - Only show if not approved */}
            {!isApproved && (
                <button
                    onClick={handleApprove}
                    style={approveButtonStyle}
                >
                    Approve Order for Selected Vendors
                </button>
            )}
        </div>
    );
};

export default Vendordersdetails;









